// Списъкът с артикули под един ред от хронологията. Обикновен текст, без
// чипове и без рамки — количеството и цените са mono, за да се четат
// вертикално, името е обикновен текст.

import { ExclamationOutlined } from "@ant-design/icons";

import {
  adjustedPrice,
  formatQuantity,
  itemName,
  MINUS,
} from "./billOperationsFormat";
import { fmtEuro } from "../../../../utils/currency";
import { parseDecimal } from "../../../../components/dataTable";
import type { OpItem } from "../../../../api/billOperations";

type Props = {
  items: OpItem[];
  language: string;
  /** Сторно — редовете са червени и сумата им е с минус. */
  negative?: boolean;
  /**
   * Знаковият процент на промяна на цената (> 0 = поевтиняване). Подава се
   * САМО при `custom_price` — тогава редът показва „стара → нова цена".
   */
  priceChangePercent?: number;
};

export const BillOperationItems = ({
  items,
  language,
  negative = false,
  priceChangePercent,
}: Props) => (
  <div className="flex flex-col gap-1">
    {items.map((item) => (
      <div key={item.bill_element_item_id} className="flex flex-col gap-1">
        <ItemLine
          name={itemName(item.item_names, language)}
          quantity={item.quantity}
          price={item.price}
          total={item.total_sum}
          negative={negative}
          priceChangePercent={priceChangePercent}
          comments={item.comments ?? []}
        />
        <ItemModifiers
          modifiers={(item.modifier_names ?? []).map((names) =>
            itemName(names, language),
          )}
        />
        {item.additions.map((addition) => (
          <div key={addition.addition_id} className="pl-4">
            <ItemLine
              name={`+ ${itemName(addition.item_names, language)}`}
              quantity={addition.quantity}
              price={addition.price}
              total={addition.total_sum}
              muted
              // Добавката носи СОБСТВЕН процент — при промяна на цена на
              // продукта тя може да е поскъпнала с друг процент или изобщо.
              priceChangePercent={
                priceChangePercent === undefined
                  ? undefined
                  : parseDecimal(addition.percent)
              }
            />
          </div>
        ))}
      </div>
    ))}
  </div>
);

/**
 * Модификаторите — червени, под реда и преди добавките. Коментарът към
 * продукта е до името (червен курсив в кавички), за да не се бъркат.
 */
const ItemModifiers = ({ modifiers }: { modifiers: string[] }) =>
  modifiers.length > 0 ? (
    <p className="m-0 flex items-start gap-1 pl-4 text-[12px] leading-[1.35] text-danger-ink">
      {/* Иконка, не символ „!": в шрифта на POS-а знакът излиза сплескан.
          Кутийката е широка колкото цифра от количеството на добавките
          (същият mono шрифт и размер), за да стои центрирана под тях. */}
      <span className="mt-[2px] flex w-[1ch] shrink-0 justify-center font-pos-mono text-[12.5px]">
        <ExclamationOutlined
          className="text-[12px] [&_svg]:stroke-current [&_svg]:stroke-[40]"
          aria-hidden
        />
      </span>
      <span className="min-w-0">{modifiers.join(", ")}</span>
    </p>
  ) : null;

const ItemLine = ({
  name,
  quantity,
  price,
  total,
  negative = false,
  muted = false,
  priceChangePercent,
  comments = [],
}: {
  name: string;
  quantity: string;
  price: string;
  total: string;
  negative?: boolean;
  muted?: boolean;
  priceChangePercent?: number;
  /** Свободният текст към продукта — изписва се до името. */
  comments?: string[];
}) => {
  const quantityValue = parseDecimal(quantity);
  // Цената КЪМ МОМЕНТА на промяната; `null` = редът не е от такова събитие.
  const changedPrice =
    priceChangePercent !== undefined && priceChangePercent !== 0
      ? adjustedPrice(price, priceChangePercent)
      : null;
  const showsPriceChange = changedPrice !== null;
  // Единичната цена излиза само когато редът не се чете от тотала: при
  // количество над едно и при ред с отстъпка (тотал ≠ цена). При промяна на
  // цена я носи стрелката „стара → нова" и втори път би било шум.
  const showsUnitPrice =
    !showsPriceChange &&
    (quantityValue !== 1 || parseDecimal(price) !== parseDecimal(total));

  return (
    <div
      className={`flex items-baseline gap-2 leading-[1.5] ${
        muted ? "text-[12.5px] text-soft" : "text-[13px]"
      }`}
    >
      <span className="shrink-0 font-pos-mono tabular-nums text-muted">
        {formatQuantity(quantity)} ×
      </span>
      {/* С коментар редът се пренася, иначе дългият текст би се отрязал. */}
      <span
        className={`min-w-0 flex-1 ${
          comments.length > 0 ? "break-words" : "truncate"
        } ${negative ? "text-danger-ink" : muted ? "text-soft" : "text-ink"}`}
      >
        {name}
        {comments.map((comment, index) => (
          <span
            key={`comment-${index}`}
            className="text-[12.5px] italic text-danger-ink"
          >
            {` – „${comment}“`}
          </span>
        ))}
      </span>
      {changedPrice !== null ? (
        <span className="shrink-0 whitespace-nowrap font-pos-mono text-[12px] tabular-nums text-violet">
          {fmtEuro(price)} → {fmtEuro(changedPrice)}
        </span>
      ) : null}
      {showsUnitPrice ? (
        <span className="shrink-0 font-pos-mono text-[11.5px] tabular-nums text-muted">
          {fmtEuro(price)}
        </span>
      ) : null}
      <span
        className={`shrink-0 font-pos-mono tabular-nums ${
          negative ? "text-danger-ink" : muted ? "text-soft" : "text-ink"
        }`}
      >
        {negative ? MINUS : ""}
        {fmtEuro(total)}
      </span>
    </div>
  );
};
